/**
 * `InviteDeveloperPanel` — ADR-0168. The "evaluator ≠ installer" escape
 * hatch on the Install + verify step: the merchant who can't edit their
 * site's theme sends the person who can an invite from right here.
 *
 * Sends a standard team invite (`POST /v1/users`) with `role: 'admin'`
 * (site mutations the installer needs — rotate key, proof — are
 * owner|admin-gated) and the ADR-0168 install context, so the email
 * names this site's domain and points the developer at the setup guide
 * after accepting. The snippet/site key are NEVER emailed — the
 * developer retrieves them in the dashboard (see ADR-0168 §Context).
 *
 * Collapsed disclosure by default — the panel must not compete with the
 * primary install path for merchants who can do it themselves.
 */

import { ChevronDown, ChevronRight, Mail, RefreshCw, UserPlus } from 'lucide-react';
import { useState } from 'react';

import type { Site } from '@sill/types';

import { inviteUser } from '../../api/users.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";

export interface InviteDeveloperPanelProps {
  site: Site;
}

export function InviteDeveloperPanel({
  site,
}: InviteDeveloperPanelProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (http === null) return null;

  const send = async (): Promise<void> => {
    const trimmed = email.trim();
    if (trimmed.length === 0 || sending) return;
    setSending(true);
    try {
      const result = await inviteUser(http, {
        email: trimmed,
        role: 'admin',
        invite_context: { kind: 'install', site_id: site.site_id },
      });
      switch (result.kind) {
        case 'ok':
          setSent(true);
          toast({
            tone: 'lime',
            title: 'Invite sent.',
            body: `${trimmed} received setup instructions for ${site.domain}. The link expires in 7 days.`,
          });
          break;
        case 'conflict':
          toast({
            tone: 'amber',
            title: 'That email already has a Sill account',
            body: 'Each email belongs to one Sill workspace, so it cannot be invited to this one. If they are already on your team, they can just sign in. Otherwise, use an email address they have not used with Sill.',
          });
          break;
        case 'forbidden':
          toast({
            tone: 'coral',
            title: 'Not allowed',
            body: 'Only owners and admins can invite teammates.',
          });
          break;
        case 'rate_limited':
          toast({
            tone: 'amber',
            title: 'Invite limit reached',
            body: 'Your account hit its daily invite limit. Try again tomorrow.',
          });
          break;
        case 'bad_request':
          toast({
            tone: 'coral',
            title: 'Could not send invite',
            body: 'Check the email address and try again.',
          });
          break;
        default:
          toast({
            tone: 'coral',
            title: 'Could not send invite',
            body: result.kind === 'error' ? result.message : 'Unknown error',
          });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center justify-between w-full"
        style={{
          background: 'transparent',
          border: 'none',
          padding: '10px 12px',
          cursor: 'pointer',
          color: C.text,
          fontFamily: sansFont,
          fontSize: 13,
          textAlign: 'left',
        }}
        data-testid="invite-developer-toggle"
      >
        <span className="inline-flex items-center" style={{ gap: 8 }}>
          <UserPlus size={14} color={C.muted} />
          <span>
            Someone else edits your site?
            <span style={{ color: C.muted }}> Invite your developer</span>
          </span>
        </span>
        {open ? (
          <ChevronDown size={12} color={C.muted} />
        ) : (
          <ChevronRight size={12} color={C.muted} />
        )}
      </button>
      {open ? (
        <div
          className="px-3 pb-3"
          style={{ borderTop: `1px solid ${C.borderSoft}`, paddingTop: 12 }}
          data-testid="invite-developer-body"
        >
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              lineHeight: 1.55,
              marginBottom: 12,
              maxWidth: '60ch',
            }}
          >
            They get admin access and an email linking to this step. No
            keys are sent by email.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setSent(false);
              }}
              placeholder="developer@example.com"
              aria-label="Developer email"
              className="px-3 py-2 flex-1"
              style={{
                background: C.bg,
                border: `1px solid ${C.border}`,
                color: C.text,
                fontFamily: sansFont,
                fontSize: 13,
                borderRadius: 2,
                maxWidth: 340,
              }}
              data-testid="invite-developer-email"
            />
            <button
              type="button"
              onClick={() => void send()}
              disabled={sending || sent || email.trim().length === 0}
              className="flex items-center justify-center gap-2 px-4 py-2"
              style={{
                background: sent ? C.limeDim : C.lime,
                color: sent ? C.limeFg : C.ink,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2,
                opacity:
                  sending || (email.trim().length === 0 && !sent) ? 0.6 : 1,
                border: sent ? `1px solid ${C.limeFg}` : 'none',
              }}
              data-testid="invite-developer-send"
            >
              {sending ? (
                <>
                  <RefreshCw
                    size={13}
                    style={{ animation: 'spin 1.4s linear infinite' }}
                  />
                  Sending…
                </>
              ) : sent ? (
                'Invite sent'
              ) : (
                <>
                  <Mail size={13} /> Send invite
                </>
              )}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
